import API_BASE from '../config';
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Search, CheckCircle2, XCircle,
  Loader2, AlertTriangle, FileText, Calendar,
  Building2, Award, ArrowRight
} from "lucide-react";
import axios from "axios";

interface VerifyResult {
  verified:     boolean;
  verify_id:    string;
  company_name: string;
  report_type:  string;
  generated_at: string;
  tier_at_gen:  string;
  risk_score?:  number;
}

export default function Verify() {
  const navigate = useNavigate();
  const [verifyId,  setVerifyId]  = useState("");
  const [status,    setStatus]    = useState<"idle" | "loading" | "found" | "not_found" | "error">("idle");
  const [result,    setResult]    = useState<VerifyResult | null>(null);
  const [errorMsg,  setErrorMsg]  = useState("");

  const handleVerify = async () => {
    const id = verifyId.trim().toUpperCase();
    if (!id) return;
    setStatus("loading");
    setResult(null);
    setErrorMsg("");
    try {
      const res = await axios.get(
        `${API_BASE}/api/reports/verify/${id}`
      );
      setResult(res.data);
      setStatus("found");
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setStatus("not_found");
      } else {
        setStatus("error");
        setErrorMsg("Verification service unavailable. Please try again.");
      }
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric"
    });

  const tierLabel = (tier: string) => {
    const map: Record<string, string> = {
      standard:     "Starter",
      starter:      "Starter",
      professional: "Professional",
      enterprise:   "Enterprise",
    };
    return map[tier?.toLowerCase()] || tier;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-['Inter']">

      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div onClick={() => navigate("/")} className="flex items-center gap-3 cursor-pointer group">
            <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-200 group-hover:scale-105 transition-transform">
              <span className="text-white font-black text-base">V</span>
            </div>
            <span className="text-xl font-black tracking-tighter text-slate-900 uppercase">VicShield</span>
          </div>
          <button onClick={() => navigate("/")}
            className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors">
            ← Back to vicshield.com
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#0f172a] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-red-600/10 border border-red-600/20 rounded-full px-4 py-1.5 mb-6">
            <ShieldCheck size={14} className="text-red-400" />
            <span className="text-[10px] font-black uppercase tracking-widest text-red-400">Report Authentication</span>
          </div>
          <h1 className="text-4xl font-black text-white tracking-tighter uppercase mb-4">
            Verify A VicShield Report
          </h1>
          <p className="text-slate-400 font-medium max-w-xl mx-auto leading-relaxed">
            Enter the Report ID from any VicShield intelligence report to confirm
            it is genuine, unmodified, and was generated by VicShield.
          </p>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-2xl mx-auto px-6 py-12">

        {/* Search box */}
        <div className="bg-white rounded-[2rem] border-2 border-slate-100 shadow-sm p-8 mb-6">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
            Report Verification ID
          </p>
          <p className="text-sm font-medium text-slate-500 mb-6 leading-relaxed">
            Find the Report ID on the cover page or certification page of your
            VicShield report. It looks like: <strong className="text-slate-900 font-mono">VS-A3F7B2C9-ENT</strong>
          </p>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={verifyId}
                onChange={e => setVerifyId(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && handleVerify()}
                placeholder="e.g. VS-A3F7B2C9-ENT"
                maxLength={16}
                className="w-full pl-11 pr-4 py-3.5 border-2 border-slate-200 rounded-xl
                  text-slate-900 text-sm font-mono font-bold placeholder:text-slate-300
                  placeholder:font-normal focus:outline-none focus:border-red-400
                  focus:ring-2 focus:ring-red-100 transition-all tracking-widest uppercase"
              />
            </div>
            <button
              onClick={handleVerify}
              disabled={status === "loading" || !verifyId.trim()}
              className="bg-red-600 text-white px-6 py-3.5 rounded-xl font-black text-[11px]
                uppercase tracking-widest hover:bg-slate-900 transition-all active:scale-95
                disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2
                shadow-lg shadow-red-200 whitespace-nowrap"
            >
              {status === "loading"
                ? <><Loader2 size={15} className="animate-spin" /> Verifying</>
                : <>Verify Report</>
              }
            </button>
          </div>
        </div>

        {/* Results */}
        <AnimatePresence mode="wait">

          {/* VERIFIED */}
          {status === "found" && result && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-[2rem] border-2 border-emerald-200 shadow-sm overflow-hidden"
            >
              {/* Green header */}
              <div className="bg-emerald-500 px-8 py-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <CheckCircle2 size={22} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-black text-lg uppercase tracking-tight">
                      ✅ Verified — Genuine VicShield Report
                    </p>
                    <p className="text-emerald-100 text-[11px] font-bold uppercase tracking-widest mt-0.5">
                      This report is authentic and unmodified
                    </p>
                  </div>
                </div>
              </div>

              {/* Report details */}
              <div className="px-8 py-6 space-y-4">
                {[
                  { icon: Award,     label: "Report ID",     value: result.verify_id },
                  { icon: Building2, label: "Organisation",  value: result.company_name || "Confidential" },
                  { icon: FileText,  label: "Report Type",   value: result.report_type },
                  { icon: Calendar,  label: "Generated On",  value: formatDate(result.generated_at) },
                  { icon: ShieldCheck, label: "Plan",        value: tierLabel(result.tier_at_gen) },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
                    <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon size={15} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        {item.label}
                      </p>
                      <p className="text-sm font-black text-slate-900">
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer note */}
              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100">
                <p className="text-[11px] font-medium text-slate-500 text-center leading-relaxed">
                  This verification confirms the report was generated by VicShield
                  and has not been altered. For questions contact{" "}
                  <a href="mailto:support@vicshield.com" className="text-red-600 font-bold">
                    support@vicshield.com
                  </a>
                </p>
              </div>
            </motion.div>
          )}

          {/* NOT FOUND */}
          {status === "not_found" && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-[2rem] border-2 border-red-200 shadow-sm overflow-hidden"
            >
              <div className="bg-red-600 px-8 py-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <XCircle size={22} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-black text-lg uppercase tracking-tight">
                      ❌ Report Not Found
                    </p>
                    <p className="text-red-100 text-[11px] font-bold uppercase tracking-widest mt-0.5">
                      This ID does not match any VicShield report
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-8 py-6">
                <p className="text-slate-500 text-sm font-medium leading-relaxed mb-4">
                  The Report ID <strong className="text-slate-900 font-mono">{verifyId}</strong> was
                  not found in our system. This could mean:
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    "The Report ID was entered incorrectly — check for typos",
                    "This report was not generated by VicShield",
                    "The report may have been generated before verification was introduced",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <AlertTriangle size={13} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm font-medium text-slate-600">{item}</p>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] font-medium text-slate-400 text-center">
                  If you believe this report is genuine please contact{" "}
                  <a href="mailto:support@vicshield.com" className="text-red-600 font-bold">
                    support@vicshield.com
                  </a>
                </p>
              </div>
            </motion.div>
          )}

          {/* ERROR */}
          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-amber-50 border-2 border-amber-200 rounded-[2rem] px-8 py-6"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} className="text-amber-500" />
                <p className="text-sm font-bold text-amber-700">{errorMsg}</p>
              </div>
            </motion.div>
          )}

        </AnimatePresence>

        {/* How it works */}
        {status === "idle" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-[2rem] border-2 border-slate-100 shadow-sm p-8 mt-6"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">
              How Report Verification Works
            </p>
            <div className="space-y-5">
              {[
                { step: "1", title: "Find the Report ID", desc: "Every VicShield report contains a unique Report ID on the cover page and certification page." },
                { step: "2", title: "Enter the ID above", desc: "Type the Report ID into the field above and click Verify Report." },
                { step: "3", title: "Instant verification", desc: "VicShield checks our database and confirms the report is genuine, showing the original details." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-black text-sm">{item.step}</span>
                  </div>
                  <div>
                    <p className="text-sm font-black text-slate-900 mb-0.5">{item.title}</p>
                    <p className="text-xs font-medium text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <div className="text-center mt-8">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
            Want your own verified report?
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-3.5
              rounded-xl font-black text-[11px] uppercase tracking-widest
              hover:bg-slate-900 transition-all shadow-lg shadow-red-200"
          >
            Start Free 7-Day Trial
            <ArrowRight size={14} />
          </button>
        </div>

      </div>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 px-6 mt-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            © 2026 VicShield Global · Official domain:{" "}
            <span className="text-red-500">vicshield.com</span> only
          </p>
        </div>
      </footer>

    </div>
  );
}
